<template>
  <div class="practice-assistant-list">
    <div class="practice-assistant-list__panel">
      <div class="practice-assistant-list__header">
        <div class="practice-assistant-list__icon-wrap">
          <i class="icon-book-line practice-assistant-list__icon t-iconfont"></i>
        </div>
        <div class="practice-assistant-list__header-content">
          <div class="practice-assistant-list__title">专项训练陪练</div>
          <div class="practice-assistant-list__desc">
            创建和管理专项训练任务，下发后员工即可进行对练
          </div>
        </div>
      </div>

      <div v-loading="loading" class="practice-assistant-list__body">
        <div class="practice-assistant-list__toolbar">
          <button
            v-if="isAdd"
            class="practice-assistant-list__create-btn"
            type="button"
            @click="emit('create')"
          >
            <i class="icon-add-line t-iconfont"></i>
            <span>新建任务</span>
          </button>
        </div>

        <div v-if="taskList.length" class="practice-assistant-list__grid">
          <div v-for="item in taskList" :key="item.id" class="practice-task-card">
            <div class="practice-task-card__content">
              <div class="practice-task-card__title">{{ item.themeName }}</div>

              <div class="practice-task-card__meta">
                <div class="practice-task-card__meta-item">
                  <span class="practice-task-card__meta-label">创建时间</span>
                  <span class="practice-task-card__meta-value">
                    {{ item.createTimeText || '-' }}
                  </span>
                </div>
                <div class="practice-task-card__meta-item practice-task-card__meta-item--end-time">
                  <span class="practice-task-card__meta-label">截止时间</span>
                  <span class="practice-task-card__meta-value">
                    {{ item.endTimeText || '-' }}
                  </span>
                </div>
                <div class="practice-task-card__meta-item practice-task-card__meta-item--user">
                  <span class="practice-task-card__meta-label">派发给谁</span>
                  <el-tooltip
                    v-if="item.userRangeText"
                    :content="item.userRangeText"
                    placement="top"
                  >
                    <span
                      class="practice-task-card__meta-value practice-task-card__meta-value--ellipsis"
                    >
                      {{ item.userRangeText }}
                    </span>
                  </el-tooltip>
                  <span v-else class="practice-task-card__meta-value">-</span>
                </div>
              </div>

              <div class="practice-task-card__text">
                {{ item.themeDesc || '暂无主题说明' }}
              </div>
            </div>

            <div class="practice-task-card__actions">
              <el-button
                v-if="isUpdate"
                class="practice-task-card__action"
                :disabled="item.statusInfo.disabled"
                link
                type="primary"
                @click="emit('redispatch', item)"
              >
                再次派发
              </el-button>
              <el-button
                v-if="isDelete"
                class="practice-task-card__action"
                :disabled="item.statusInfo.disabled"
                link
                type="primary"
                @click="handleDelete(item)"
              >
                删除
              </el-button>
            </div>
          </div>
        </div>

        <div v-else class="practice-assistant-list__empty">
          <ListEmpty title="暂无主题陪练任务" />
        </div>

        <div v-if="hasMore" class="practice-assistant-list__more" @click="handleLoadMore">
          <span v-if="!moreLoading">加载更多</span>
          <span v-else><i class="icon-loader-2-line t-iconfont"></i> 加载中...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import dayjs from 'dayjs'
import { ElMessage, ElMessageBox } from 'element-plus'
import ListEmpty from '@/pages/ai-board/components/list-empty.vue'

import {
  deleteCheckThemePracticeTask,
  deleteThemePracticeTask,
  getThemePracticeTaskList
} from '@/api/index.js'

defineOptions({
  name: 'PracticeAssistantList'
})

const PAGE_SIZE = 8

const getResponsePayload = (response) => {
  return response?.result || response?.data || {}
}

// 任务状态：0 生成中 1 成功 2 失败 3 部分派发，失败时展示 failReason
const STATUS_MAP = {
  0: { text: '画像生成中...', type: 'processing', spin: false, showTooltip: false, disabled: true },
  1: { text: '已派发', type: 'success', spin: false, showTooltip: false },
  2: { text: '生成失败', type: 'danger', spin: false, showTooltip: true },
  3: { text: '部分派发', type: 'warning', spin: false, showTooltip: true }
}

const getStatusInfo = (status) => {
  return (
    STATUS_MAP[Number(status)] || {
      text: '',
      type: 'default',
      spin: false,
      showTooltip: false
    }
  )
}

const getThemeDescText = (themeDesc) => {
  return typeof themeDesc === 'string' ? themeDesc : ''
}

const emit = defineEmits(['create', 'redispatch'])

const loading = ref(false)
const moreLoading = ref(false)
const currentPage = ref(1)
const total = ref(0)
const taskList = ref([])
const isAdd = ref(1)
const isUpdate = ref(1)
const isDelete = ref(1)

const hasMore = computed(() => {
  return taskList.value.length < total.value
})

const formatDateTime = (value) => {
  if (!value) {
    return ''
  }

  let timeValue = value
  if (typeof timeValue === 'string' && /^\d+$/.test(timeValue)) {
    timeValue = Number(timeValue)
  }
  if (typeof timeValue === 'number' && `${Math.abs(timeValue)}`.length === 10) {
    timeValue *= 1000
  }

  const date = dayjs(timeValue)
  return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : ''
}

const getRangeFallbackName = (item = {}) => {
  const rangeTypeText = item.property === 'dept' ? '部门' : '成员'
  return item.id ? `${rangeTypeText}(${item.id})` : rangeTypeText
}

const normalizeUserRange = (userRange) => {
  if (!Array.isArray(userRange)) {
    return []
  }

  return userRange
    .map((item) => {
      const normalizedItem = item || {}
      const id = normalizedItem.id || normalizedItem.userId || normalizedItem.deptId || ''
      const property = normalizedItem.property || 'user'

      return {
        ...normalizedItem,
        id,
        property,
        name:
          normalizedItem.name ||
          normalizedItem.userName ||
          normalizedItem.deptName ||
          normalizedItem.label ||
          getRangeFallbackName({
            id,
            property
          })
      }
    })
    .filter((item) => item.id)
}

const getUserRangeText = (userRange) => {
  if (typeof userRange === 'string') {
    return userRange
  }
  if (!Array.isArray(userRange)) {
    return ''
  }
  return userRange
    .map((item) => item?.name)
    .filter(Boolean)
    .join('、')
}

const normalizeListItem = (item = {}) => {
  const createTime = item.addTime || item.createTime || item.time || item.updateTime || ''
  const dueTime = item.dueTime || ''
  const id = item.id || item.themeTaskId || ''
  const userRangePojoList = normalizeUserRange(item.userRangePojoList || item.trainerUser)
  const themeDesc = getThemeDescText(item.themeDesc)
  // 兼容后端字段拼写，fialReason 为接口实际返回字段
  const failReason = item.fialReason || item.failReason || ''

  return {
    ...item,
    id,
    themeName: item.themeName || '未命名任务',
    createTime,
    createTimeText: formatDateTime(createTime),
    dueTime,
    endTimeText: formatDateTime(dueTime),
    userRangePojoList,
    userRangeText: getUserRangeText(userRangePojoList),
    themeDesc,
    failReason,
    statusInfo: getStatusInfo(item.status)
  }
}

const fetchList = async (isMore = false) => {
  const targetPage = isMore ? currentPage.value + 1 : 1

  if (isMore) {
    moreLoading.value = true
  } else {
    loading.value = true
  }

  try {
    const res = await getThemePracticeTaskList({
      page: targetPage,
      pageSize: PAGE_SIZE
    })
    const result = getResponsePayload(res)
    const list = (result.list || []).map((item) => normalizeListItem(item))
    const rowsCount = result.pageHelper?.rowsCount

    isAdd.value = result.isAdd ?? 1
    isUpdate.value = result.isUpdate ?? 1
    isDelete.value = result.isDelete ?? 1
    total.value = Number(rowsCount) || list.length
    currentPage.value = targetPage
    taskList.value = isMore ? [...taskList.value, ...list] : list
  } finally {
    loading.value = false
    moreLoading.value = false
  }
}

const handleLoadMore = () => {
  if (!hasMore.value || moreLoading.value) {
    return
  }
  fetchList(true)
}

const getDeleteConfirmMessage = (hasInProgressPractice) => {
  if (hasInProgressPractice) {
    return '当前任务还有员工在练习中，是否需要删除？'
  }
  return '删除后，该专项训练不可恢复，确定删除吗？'
}

const handleDelete = async (item) => {
  if (!item?.id || !isDelete.value) {
    return
  }

  const deleteCheckRes = await deleteCheckThemePracticeTask({
    id: item.id
  })
  const deleteCheckResult = getResponsePayload(deleteCheckRes)

  await ElMessageBox.confirm(
    getDeleteConfirmMessage(deleteCheckResult.hasInProgressPractice),
    '删除',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }
  )

  await deleteThemePracticeTask({
    id: item.id
  })
  ElMessage.success('删除成功')
  await fetchList()
}

onMounted(() => {
  fetchList()
})

defineExpose({
  fetchList
})
</script>

<style lang="scss" scoped>
// 文字/Text-4 强调正文标题
$text-strong: #323233;
// 文字/Text-3 次强调正文标题
$text-secondary: #646566;
// 文字/Text-2 次要信息
$text-placeholder: #969799;
// 链接/Link-6 常规
$link-primary: #248df5;
// 拓展色/紫罗兰/Violets-6
$violet-6: #7a52f4;
// 线条/Line-1 浅线条
$line-light: #f2f3f5;
// 线条/Line-2 一般线条
$line-normal: #e5e6eb;
// 填充/纯白
$fill-white: #ffffff;
// 填充/Fill-1 浅色 禁用背景
$fill-soft: #f7f8fa;
// 三级投影/高
$shadow-card: 0px 2px 30px 2px rgba(0, 0, 0, 0.05);

.practice-assistant-list {
  width: 100%;
  height: 100%;
  padding: 16px 0;
  box-sizing: border-box;
  overflow-y: auto;
  display: flex;
  justify-content: center;

  &__panel {
    width: 1120px;
    height: max-content;
    border: 1px solid $line-normal;
    border-radius: 12px;
    background: $fill-white;
    box-shadow: $shadow-card;
    overflow: hidden;
  }

  &__header {
    display: flex;
    align-items: center;
    padding: 12px;
    border-bottom: 1px solid $line-normal;
    background: $fill-white;
  }

  &__icon-wrap {
    width: 32px;
    height: 32px;
    margin-right: 12px;
    border-radius: 8px;
    background: $fill-white;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__icon {
    font-size: 20px;
    line-height: 20px;
    color: $text-strong;
  }

  &__header-content {
    flex: 1;
    min-width: 0;
  }

  &__title {
    margin-bottom: 4px;
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
    color: $text-strong;
  }

  &__desc {
    font-size: 14px;
    line-height: 20px;
    color: $text-placeholder;
  }

  &__body {
    padding: 16px;
    background: $fill-white;
  }

  &__toolbar {
    margin-bottom: 16px;
  }

  &__create-btn {
    height: 32px;
    padding: 0 12px;
    border: none;
    border-radius: 8px;
    background: $violet-6;
    color: $fill-white;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    line-height: 20px;
    cursor: pointer;

    .t-iconfont {
      margin-right: 8px;
      font-size: 16px;
      line-height: 16px;
    }

    &:hover {
      opacity: 0.92;
    }
  }

  &__grid {
    display: flex;
    flex-direction: column;
  }

  &__empty {
    min-height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__more {
    margin-top: 16px;
    text-align: center;
    font-size: 12px;
    line-height: 16px;
    color: $link-primary;
    cursor: pointer;
    user-select: none;

    .t-iconfont {
      margin-right: 4px;
      display: inline-block;
      animation: practice-assistant-spin 1.2s linear infinite;
    }
  }
}

.practice-task-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 120px;
  padding: 16px;
  border: 1px solid $line-normal;
  border-radius: 16px;
  background: $fill-white;
  box-sizing: border-box;

  & + & {
    margin-top: 12px;
  }

  &__content {
    flex: 1;
    min-width: 0;
    padding-right: 24px;
  }

  &__title {
    margin-bottom: 8px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    color: $text-strong;
  }

  &__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }

  &__meta-item {
    min-width: 0;
    display: flex;
    align-items: center;
    font-size: 12px;
    line-height: 16px;
    margin-right: 16px;
    margin-bottom: 8px;
  }

  &__meta-item + &__meta-item {
    padding-left: 16px;
    border-left: 1px solid $line-normal;
  }

  &__meta-item--user {
    flex: 1;
    margin-right: 0;
  }

  &__meta-label {
    flex-shrink: 0;
    margin-right: 12px;
    color: $text-secondary;
  }

  &__meta-value {
    color: $text-strong;
  }

  &__meta-value--ellipsis {
    display: inline-block;
    max-width: 280px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: top;
  }

  &__text {
    min-height: 40px;
    font-size: 14px;
    line-height: 20px;
    color: $text-placeholder;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  &__actions {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    margin-top: 2px;
    margin-left: 16px;
  }

  &__action {
    padding: 0;
    font-size: 14px;
    line-height: 20px;
    color: $link-primary;
    cursor: pointer;
    user-select: none;

    & + & {
      margin-left: 16px;
    }

    &:hover {
      opacity: 0.85;
    }
  }
}

@keyframes practice-assistant-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
